import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Truck,
  Package,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Building2,
  MoreHorizontal,
  Send,
  PackageCheck,
  Receipt,
  XCircle,
  Edit,
  Trash2,
  FileText,
  Printer,
  PlusCircle,
  Eye,
  Mail,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/billingUtils";
import {
  SUPPLIER_ORDER_STATUS_COLORS,
  SUPPLIER_ORDER_STATUS_LABELS,
} from "@/types/supplierOrders";
import type {
  SupplierOrder,
  SupplierOrderStatus,
  SupplierOrderFormData,
  SupplierOrderLineFormData,
} from "@/types/supplierOrders";
import { ConsolidationAlert, SupplierOrderPDF, CreateMaterialDialog } from "@/components/SupplierOrders";
import { useReactToPrint } from "react-to-print";
import { useRef } from "react";
import { SupplierSelector, type SupplierOption } from "@/components/Suppliers/SupplierSelector";
import { SupplierFormDialog } from "@/components/Suppliers/SupplierFormDialog";

const DEFAULT_NOTES = `Es imprescindible que nos envíen los certificados de conformidad de los materiales suministrados.
Será necesario el suministro de las fichas técnicas de los materiales suministrados e informar en caso de que estas sufran alguna actualización.`;

const emptyLine: SupplierOrderLineFormData = {
  description: '',
  unit: 'UND',
  quantity_ordered: 1,
  unit_price: 0,
  tax_rate: 21,
  discount_percent: 0,
};

const emptyOrder: SupplierOrderFormData & { 
  supplier_id?: string;
  supplier_email?: string;
  supplier_phone?: string;
  supplier_address_line1?: string;
  supplier_address_line2?: string;
  supplier_postal_code?: string;
  supplier_city?: string;
  supplier_province?: string;
} = {
  supplier_id: undefined,
  supplier_name: '',
  supplier_tax_id: '',
  supplier_email: '',
  supplier_phone: '',
  supplier_address_line1: '',
  supplier_address_line2: '',
  supplier_postal_code: '',
  supplier_city: '',
  supplier_province: '',
  order_date: new Date().toISOString().split('T')[0],
  expected_date: '',
  notes: DEFAULT_NOTES,
  lines: [{ ...emptyLine }],
};


export default function SupplierOrders() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open dialog when coming from dashboard alerts
  useEffect(() => {
    const create = searchParams.get('create');
    const supplier = searchParams.get('supplier');
    if (create === 'true') {
      setEditingOrder({
        ...emptyOrder,
        supplier_name: supplier ? decodeURIComponent(supplier) : '',
      });
      setDialogOpen(true);
      // Clear URL params
      setSearchParams({});
    }
  }, []);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<"activos" | "historico">("activos");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<SupplierOrderFormData>(emptyOrder);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newSupplierDialogOpen, setNewSupplierDialogOpen] = useState(false);

  // Print states
  const [printOrder, setPrintOrder] = useState<SupplierOrder | null>(null);
  const [printLanguage, setPrintLanguage] = useState<"es" | "en">("es");
  const [printMode, setPrintMode] = useState<"po" | "receipt">("po");
  const [previewOpen, setPreviewOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: printOrder ? `PO-${printOrder.order_number}` : "PO",
  });

  const openPrintDialog = (order: SupplierOrder, lang: "es" | "en" = "es", mode: "po" | "receipt" = "po") => {
    setPrintOrder(order);
    setPrintLanguage(lang);
    setPrintMode(mode);
    setTimeout(() => handlePrint(), 100);
  };

  const openEmailWithOrder = (order: SupplierOrder) => {
    const supplierEmail = (order as any).supplier_email || "";
    const subject = encodeURIComponent("Pedido " + order.order_number + " - Magma Composites S.L.");
    const body = encodeURIComponent([
      "Estimado proveedor,",
      "",
      "Adjunto enviamos el pedido de compra " + order.order_number + ".",
      "",
      "Fecha: " + format(new Date(order.order_date), "dd/MM/yyyy"),
      "Importe total: " + (order.total_amount || 0).toFixed(2) + " EUR",
      "",
      "Por favor confirme recepcion y fecha estimada de entrega.",
      "",
      "Saludos cordiales,",
      "Magma Composites S.L."
    ].join("\n"));
  };

  // Create/Edit material dialog
  const [createMaterialOpen, setCreateMaterialOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<any>(null);
  const [currentLineIdx, setCurrentLineIdx] = useState<number | null>(null);

  const handleCreateMaterial = (lineIdx: number) => {
    setCurrentLineIdx(lineIdx);
    setEditingMaterial(null);
    setCreateMaterialOpen(true);
  };

  const handleEditMaterial = (lineIdx: number, materialId: string) => {
    const material = allMaterials.find((m: any) => m.id === materialId);
    if (material) {
      setCurrentLineIdx(lineIdx);
      setEditingMaterial(material);
      setCreateMaterialOpen(true);
    }
  };

  const handleMaterialCreated = (material: any) => {
    // Refresh materials query
    queryClient.invalidateQueries({ queryKey: ["all-materials-supplier-orders"] });
    // Auto-select the new material in the current line
    if (currentLineIdx !== null) {
      updateLine(currentLineIdx, 'raw_material_id', material.id);
      updateLine(currentLineIdx, 'code', material.code || '');
      updateLine(currentLineIdx, 'description', material.name);
      updateLine(currentLineIdx, 'unit', material.unit || 'UND');
      if (material.cost_per_unit) {
        updateLine(currentLineIdx, 'unit_price', material.cost_per_unit);
      }
    }
  };

  // Fetch raw_materials AND material_inventory (for material selector in lines)
  const { data: allMaterials = [] } = useQuery({
    queryKey: ["all-materials-supplier-orders"],
    queryFn: async () => {
      // Fetch raw_materials
      const { data: rawMats, error: err1 } = await api
        .from("raw_materials")
        .select("id, code, name, supplier, supplier_id, billing_customer_id, unit")
        .eq("is_active", true)
        .order("name");
      if (err1) throw err1;

      // Fetch material_inventory (prepregs)
      const { data: invMats, error: err2 } = await api
        .from("material_inventory")
        .select("id, code, name, supplier")
        .order("name");
      if (err2) throw err2;

      // Combine both, marking source
      const combined = [
        ...(rawMats || []).map((m: any) => ({ ...m, _source: 'raw' })),
        ...(invMats || []).map((m: any) => ({ ...m, _source: 'inventory', unit: 'M2' })),
      ];
      return combined;
    },
  });

  // Use supplier_id directly from billing_customers (no intermediate mapping needed)
  const currentSupplierId = editingOrder.supplier_id || null;

  // Filter materials by selected supplier using billing_customer_id
  const filteredMaterials = useMemo(() => {
    // Si no hay proveedor seleccionado, mostrar todos los materiales
    if (!editingOrder.supplier_id) return allMaterials;

    const filtered = allMaterials.filter((m: any) => {
      // Match by billing_customer_id (direct FK to billing_customers)
      if (m.billing_customer_id === editingOrder.supplier_id) return true;

      // Fallback: match by supplier text field for legacy materials
      if (editingOrder.supplier_name && m.supplier) {
        const supplierNameLower = editingOrder.supplier_name.toLowerCase();
        const matSupplierLower = m.supplier.toLowerCase();
        if (matSupplierLower.includes(supplierNameLower)) return true;
        if (supplierNameLower.includes(matSupplierLower)) return true;
      }

      return false;
    });

    // Si no hay coincidencias, mostrar todos los materiales
    return filtered;  // Solo materiales del proveedor seleccionado
  }, [allMaterials, editingOrder.supplier_id, editingOrder.supplier_name]);

  // Fetch orders
  const { data: orders = [], isLoading } = useQuery<SupplierOrder[]>({
    queryKey: ["supplier-orders", statusFilter],
    queryFn: async () => {
      let query = api
        .from("supplier_orders")
        .select('*,lines:supplier_order_lines(*),supplier:billing_customers(address_line1,address_line2,postal_code,city,province,email,phone,contact_name)')
        .order("order_date", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query.limit(200);
      if (error) throw error;
      return data || [];
    },
  });

  // Filter by search
  const filteredOrders = useMemo(() => {
    const activeStatuses = ["draft", "pending", "sent", "partial"];
    const historicStatuses = ["received", "invoiced", "cancelled"];
    
    let result = orders.filter(o => 
      activeTab === "activos" 
        ? activeStatuses.includes(o.status)
        : historicStatuses.includes(o.status)
    );
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(o =>
        o.order_number?.toLowerCase().includes(term) ||
        o.supplier_name.toLowerCase().includes(term)
      );
    }
    return result;
  }, [orders, searchTerm, activeTab]);

  // Stats
  const stats = useMemo(() => {
    const pending = orders.filter(o => o.status === 'pending').length;
    const inTransit = orders.filter(o => o.status === 'sent').length;
    const partial = orders.filter(o => o.status === 'partial').length;
    const thisMonth = orders
      .filter(o => o.order_date?.startsWith(new Date().toISOString().slice(0, 7)))
      .reduce((sum, o) => sum + (o.total_amount || 0), 0);
    return { pending, inTransit, partial, thisMonth };
  }, [orders]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!editingOrder.supplier_name) {
        throw new Error("El proveedor es obligatorio");
      }

      if (editingId) {
        const { error: orderError } = await api
          .from("supplier_orders")
          .update({
            supplier_name: editingOrder.supplier_name,
            supplier_tax_id: editingOrder.supplier_tax_id,
            order_date: editingOrder.order_date,
            expected_date: editingOrder.expected_date || null,
            notes: editingOrder.notes,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingId);
        if (orderError) throw orderError;

        await api.from("supplier_order_lines").delete().eq("order_id", editingId);

        if (editingOrder.lines.length > 0) {
          const { error: linesError } = await api
            .from("supplier_order_lines")
            .insert(editingOrder.lines.map((line, idx) => ({
              order_id: editingId,
              line_number: idx + 1,
              code: line.code || null,
              description: line.description,
              unit: line.unit,
              quantity_ordered: line.quantity_ordered,
              unit_price: line.unit_price,
              tax_rate: line.tax_rate,
              discount_percent: line.discount_percent || 0,
              material_id: line.material_id || null,
              raw_material_id: line.raw_material_id || null,
              billing_article_id: (line as any).billing_article_id || null,
            })));
          if (linesError) throw linesError;
        }
      } else {
        const { data: newOrder, error: orderError } = await api
          .from("supplier_orders")
          .insert({
            supplier_name: editingOrder.supplier_name,
            supplier_tax_id: editingOrder.supplier_tax_id,
            supplier_id: editingOrder.supplier_id,
            order_date: editingOrder.order_date,
            expected_date: editingOrder.expected_date || null,
            notes: editingOrder.notes,
            status: 'draft',
            created_by: user?.id,
          })
          .select()
          .single();
        if (orderError) throw orderError;

        if (editingOrder.lines.length > 0) {
          const { error: linesError } = await api
            .from("supplier_order_lines")
            .insert(editingOrder.lines.map((line, idx) => ({
              order_id: newOrder.id,
              line_number: idx + 1,
              code: line.code || null,
              description: line.description,
              unit: line.unit,
              quantity_ordered: line.quantity_ordered,
              unit_price: line.unit_price,
              tax_rate: line.tax_rate,
              discount_percent: line.discount_percent || 0,
              material_id: line.material_id || null,
              raw_material_id: line.raw_material_id || null,
              billing_article_id: (line as any).billing_article_id || null,
            })));
          if (linesError) throw linesError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-orders"] });
      setDialogOpen(false);
      setEditingOrder(emptyOrder);
      setEditingId(null);
      toast.success(editingId ? "Pedido actualizado" : "Pedido creado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Status change mutation
  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: SupplierOrderStatus }) => {
      const updates: any = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'sent') {
        updates.sent_date = new Date().toISOString().split('T')[0];
      } else if (status === 'received') {
        updates.received_date = new Date().toISOString().split('T')[0];
      }

      const { error } = await api
        .from("supplier_orders")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-orders"] });
      toast.success("Estado actualizado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from("supplier_orders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-orders"] });
      toast.success("Pedido eliminado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openNewDialog = () => {
    setEditingOrder(emptyOrder);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (order: SupplierOrder) => {
    setEditingOrder({
      supplier_id: order.supplier_id,
      supplier_name: order.supplier_name,
      supplier_tax_id: order.supplier_tax_id || '',
      order_date: order.order_date,
      expected_date: order.expected_date || '',
      notes: order.notes || '',
      lines: order.lines?.map(l => ({
        id: l.id,
        material_id: l.material_id,
        raw_material_id: l.raw_material_id,
        code: l.code || '',
        description: l.description,
        unit: l.unit,
        quantity_ordered: l.quantity_ordered,
        unit_price: l.unit_price,
        tax_rate: l.tax_rate,
      })) || [{ ...emptyLine }],
    });
    setEditingId(order.id);
    setDialogOpen(true);
  };

  const addLine = () => {
    setEditingOrder({
      ...editingOrder,
      lines: [...editingOrder.lines, { ...emptyLine }],
    });
  };

  const removeLine = (idx: number) => {
    setEditingOrder({
      ...editingOrder,
      lines: editingOrder.lines.filter((_, i) => i !== idx),
    });
  };

  const updateLine = (idx: number, field: keyof SupplierOrderLineFormData, value: any) => {
    const newLines = [...editingOrder.lines];
    newLines[idx] = { ...newLines[idx], [field]: value };
    setEditingOrder({ ...editingOrder, lines: newLines });
  };

  // Update multiple fields at once to avoid state batching issues
  const updateLineMultiple = (idx: number, updates: Partial<SupplierOrderLineFormData>) => {
    const newLines = [...editingOrder.lines];
    newLines[idx] = { ...newLines[idx], ...updates };
    setEditingOrder({ ...editingOrder, lines: newLines });
  };

  const calculateLineTotal = (line: SupplierOrderLineFormData) => {
    const subtotal = line.quantity_ordered * line.unit_price;
    const discount = subtotal * ((line.discount_percent || 0) / 100);
    const subtotalWithDiscount = subtotal - discount;
    const tax = subtotalWithDiscount * (line.tax_rate / 100);
    return subtotalWithDiscount + tax;
  };

          
  const calculateOrderTotal = () => {
    return editingOrder.lines.reduce((sum, line) => sum + calculateLineTotal(line), 0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Pedidos a Proveedores</h1>
            <p className="text-muted-foreground">
              Gestión de pedidos de compra y recepción de materiales
            </p>
          </div>
          <Button onClick={openNewDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo pedido
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Truck className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.inTransit}</p>
                  <p className="text-sm text-muted-foreground">En tránsito</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.partial}</p>
                  <p className="text-sm text-muted-foreground">Recepción parcial</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.thisMonth)}</p>
                  <p className="text-sm text-muted-foreground">Este mes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por número, proveedor..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="sent">Enviado</SelectItem>
                  <SelectItem value="partial">Parcial</SelectItem>
                  <SelectItem value="received">Recibido</SelectItem>
                  <SelectItem value="invoiced">Facturado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table with Tabs */}
        <Card>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "activos" | "historico")} className="w-full">
            <div className="flex items-center justify-between px-4 pt-4">
              <TabsList>
                <TabsTrigger value="activos">Activos</TabsTrigger>
                <TabsTrigger value="historico">Histórico</TabsTrigger>
              </TabsList>
              <span className="text-sm text-muted-foreground">{filteredOrders.length} pedidos</span>
            </div>
          </Tabs>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nº Pedido</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Fecha Esperada</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Cargando pedidos...
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Truck className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">No hay pedidos a proveedores</p>
                    <Button onClick={openNewDialog}>
                      <Plus className="w-4 h-4 mr-2" />
                      Crear pedido
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow 
                    key={order.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openPrintDialog(order, "es", "po")}
                  >
                    <TableCell className="font-mono font-medium">
                      {order.order_number}
                    </TableCell>
                    <TableCell>
                      {format(new Date(order.order_date), "dd/MM/yyyy")}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{order.supplier_name}</p>
                        {order.supplier_tax_id && (
                          <p className="text-xs text-muted-foreground">{order.supplier_tax_id}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {order.expected_date
                        ? format(new Date(order.expected_date), "dd/MM/yyyy")
                        : "-"
                      }
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {formatCurrency(order.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={SUPPLIER_ORDER_STATUS_COLORS[order.status]}>
                        {SUPPLIER_ORDER_STATUS_LABELS[order.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* PDF options available for all statuses */}
                          <DropdownMenuItem onClick={() => openPrintDialog(order, "es", "po")}>
                            <FileText className="w-4 h-4 mr-2" />
                            Descargar P.O. (ES)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openPrintDialog(order, "en", "po")}>
                            <FileText className="w-4 h-4 mr-2" />
                            Download P.O. (EN)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openPrintDialog(order, "es", "receipt")}>
                            <Printer className="w-4 h-4 mr-2" />
                            Albarán de Recepción
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEmailWithOrder(order)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Enviar por Email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {order.status === 'draft' && (
                            <>
                              <DropdownMenuItem onClick={() => openEditDialog(order)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => statusMutation.mutate({ id: order.id, status: 'pending' })}
                              >
                                <Clock className="w-4 h-4 mr-2" />
                                Marcar pendiente
                              </DropdownMenuItem>
                            </>
                          )}
                          {order.status === 'pending' && (
                            <DropdownMenuItem
                              onClick={() => statusMutation.mutate({ id: order.id, status: 'sent' })}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Marcar enviado
                            </DropdownMenuItem>
                          )}
                          {(order.status === 'sent' || order.status === 'partial') && (
                            <DropdownMenuItem
                              onClick={() => statusMutation.mutate({ id: order.id, status: 'received' })}
                            >
                              <PackageCheck className="w-4 h-4 mr-2" />
                              Marcar recibido
                            </DropdownMenuItem>
                          )}
                          {order.status === 'received' && (
                            <DropdownMenuItem
                              onClick={() => statusMutation.mutate({ id: order.id, status: 'invoiced' })}
                            >
                              <Receipt className="w-4 h-4 mr-2" />
                              Vincular factura
                            </DropdownMenuItem>
                          )}
                          {!['received', 'invoiced', 'cancelled'].includes(order.status) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => statusMutation.mutate({ id: order.id, status: 'cancelled' })}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancelar
                              </DropdownMenuItem>
                            </>
                          )}
                          {order.status === 'draft' && (
                            <DropdownMenuItem
                              onClick={() => {
                                if (confirm('¿Eliminar este pedido?')) {
                                  deleteMutation.mutate(order.id);
                                }
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Order Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Editar pedido' : 'Nuevo pedido a proveedor'}
              </DialogTitle>
              <DialogDescription>
                {editingId ? 'Modifica los datos del pedido' : 'Crea un nuevo pedido de compra'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Consolidation Alert */}
              {editingOrder.supplier_id && (
                <ConsolidationAlert
                  supplierId={editingOrder.supplier_id}
                  supplierName={editingOrder.supplier_name}
                  onAddMaterial={(mat) => {
                    setEditingOrder({
                      ...editingOrder,
                      lines: [...editingOrder.lines, {
                        ...emptyLine,
                        raw_material_id: mat.id,
                        code: mat.code || "",
                        description: mat.name,
                        quantity_ordered: Math.max(1, (mat.min_stock || 0) - (mat.current_stock || 0)),
                      }],
                    });
                  }}
                />
              )}

              {/* Supplier Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Proveedor *</Label>
                  <SupplierSelector
                    value={editingOrder.supplier_id || null}
                    onValueChange={(id, supplier) => {
                      if (supplier) {
                        setEditingOrder({
                          ...editingOrder,
                          supplier_id: id,
                          supplier_name: supplier.legal_name,
                          supplier_tax_id: supplier.tax_id || '',
                          supplier_email: supplier.email || '',
                          supplier_phone: supplier.phone || '',
                          supplier_address_line1: supplier.address_line1 || '',
                          supplier_address_line2: supplier.address_line2 || '',
                          supplier_postal_code: supplier.postal_code || '',
                          supplier_city: supplier.city || '',
                          supplier_province: supplier.province || '',
                        });
                      } else {
                        setEditingOrder({
                          ...editingOrder,
                          supplier_id: undefined,
                          supplier_name: '',
                          supplier_tax_id: '',
                          supplier_email: '',
                          supplier_phone: '',
                          supplier_address_line1: '',
                          supplier_address_line2: '',
                          supplier_postal_code: '',
                          supplier_city: '',
                          supplier_province: '',
                        });
                      }
                    }}
                    allowCreate={true}
                    onCreateNew={() => setNewSupplierDialogOpen(true)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>NIF/CIF</Label>
                  <Input
                    value={editingOrder.supplier_tax_id || ''}
                    onChange={(e) => setEditingOrder({
                      ...editingOrder,
                      supplier_tax_id: e.target.value.toUpperCase()
                    })}
                    placeholder="B12345678"
                    disabled={!!editingOrder.supplier_id}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha del pedido</Label>
                  <Input
                    type="date"
                    value={editingOrder.order_date}
                    onChange={(e) => setEditingOrder({
                      ...editingOrder,
                      order_date: e.target.value
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha esperada de entrega</Label>
                  <Input
                    type="date"
                    value={editingOrder.expected_date || ''}
                    onChange={(e) => setEditingOrder({
                      ...editingOrder,
                      expected_date: e.target.value
                    })}
                  />
                </div>
              </div>

              {/* Lines */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Líneas del pedido</Label>
                  <Button size="sm" onClick={addLine}>
                    <Plus className="w-4 h-4 mr-1" />
                    Añadir línea
                  </Button>
                </div>

                <div className="border rounded-lg overflow-x-auto">
                  <Table className="min-w-[900px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Referencia</TableHead>
                        <TableHead className="min-w-[180px]">Descripción</TableHead>
                        <TableHead className="min-w-[80px] text-center">Cant.</TableHead>
                        <TableHead className="min-w-[80px] text-center">Ud.</TableHead>
                        <TableHead className="min-w-[100px] text-right">Precio €</TableHead>
                        <TableHead className="min-w-[80px] text-center">IVA</TableHead>
                        <TableHead className="min-w-[70px] text-center">Dto.%</TableHead>
                        <TableHead className="min-w-[100px] text-right">Total €</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editingOrder.lines.map((line, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex gap-1">
                                <div className="flex-1">
                                  <SearchableSelect
                                    value={line.raw_material_id || ""}
                                    onValueChange={(val) => {
                                      const mat = allMaterials.find((m: any) => m.id === val);
                                      updateLineMultiple(idx, {
                                        raw_material_id: val || null,
                                        code: mat?.code || '',
                                        description: mat?.name || '',
                                        unit: mat?.unit || 'UND',
                                      });
                                    }}
                                    placeholder="Buscar material..."
                                    options={[
                                      { value: "", label: "-- Sin referencia --" },
                                      ...filteredMaterials.map((m: any) => ({
                                        value: m.id,
                                        label: (m.code ? m.code + " - " : "") + m.name,
                                      })),
                                    ]}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9 shrink-0"
                                  onClick={() => line.raw_material_id ? handleEditMaterial(idx, line.raw_material_id) : handleCreateMaterial(idx)}
                                  title={line.raw_material_id ? "Editar material" : "Crear material"}
                                >
                                  <PlusCircle className="h-4 w-4" />
                                </Button>
                              </div>
                              <Input
                                value={line.code || ""}
                                onChange={(e) => updateLine(idx, 'code', e.target.value)}
                                placeholder="Código/Referencia"
                                className="text-sm"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={line.description}
                              onChange={(e) => updateLine(idx, 'description', e.target.value)}
                              placeholder="Descripción del artículo"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.001"
                              value={line.quantity_ordered}
                              onChange={(e) => updateLine(idx, 'quantity_ordered', parseFloat(e.target.value) || 0)}
                              className="text-center"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={line.unit}
                              onValueChange={(v) => updateLine(idx, 'unit', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="UND">UND</SelectItem>
                                <SelectItem value="KG">KG</SelectItem>
                                <SelectItem value="M">M</SelectItem>
                                <SelectItem value="M2">M²</SelectItem>
                                <SelectItem value="L">L</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.unit_price}
                              onChange={(e) => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="text-right w-full min-w-[100px]"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={String(line.tax_rate)}
                              onValueChange={(v) => updateLine(idx, 'tax_rate', parseFloat(v))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="4">4%</SelectItem>
                                <SelectItem value="10">10%</SelectItem>
                                <SelectItem value="21">21%</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              className="w-16 text-center"
                              value={line.discount_percent || 0}
                              onChange={(e) => updateLine(idx, "discount_percent", parseFloat(e.target.value) || 0)}
                            />
                          </TableCell>
                            {formatCurrency(calculateLineTotal(line))}
                          </TableCell>
                          <TableCell>
                            {editingOrder.lines.length > 1 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeLine(idx)}
                              >
                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-end">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total del pedido</p>
                    <p className="text-2xl font-bold">{formatCurrency(calculateOrderTotal())}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={editingOrder.notes || ''}
                  onChange={(e) => setEditingOrder({
                    ...editingOrder,
                    notes: e.target.value
                  })}
                  placeholder="Instrucciones especiales, observaciones..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="flex justify-between sm:justify-between">
              <Button variant="outline" onClick={() => setPreviewOpen(true)} className="gap-2">
                <Eye className="w-4 h-4" />
                Vista Previa
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Guardando...' : editingId ? 'Guardar' : 'Crear pedido'}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* New Supplier Dialog */}
      {/* Hidden print component */}
      {printOrder && (
        <div style={{ display: "none" }}>
          <SupplierOrderPDF
            ref={printRef}
            order={printOrder}
            language={printLanguage}
            hideValues={printMode === "receipt"}
          />
        </div>
      )}

      <CreateMaterialDialog
        open={createMaterialOpen}
        onOpenChange={setCreateMaterialOpen}
        supplierName={editingOrder.supplier_name}
        supplierId={currentSupplierId || undefined}
        editingMaterial={editingMaterial}
        onSuccess={handleMaterialCreated}
      />

      {/* Preview Dialog - PDF completo */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[230mm] max-h-[95vh] overflow-auto p-2">
          <DialogHeader className="sr-only">
            <DialogTitle>Vista Previa del Pedido</DialogTitle>
          </DialogHeader>
          <div className="bg-white rounded shadow-inner overflow-auto" style={{ maxHeight: "calc(95vh - 60px)" }}>
            <SupplierOrderPDF
              order={{
                id: editingId || "preview",
                order_number: editingId ? (orders.find(o => o.id === editingId)?.order_number || "BORRADOR") : "BORRADOR",
                supplier_id: editingOrder.supplier_id,
                supplier_name: editingOrder.supplier_name || "Sin proveedor",
                supplier_tax_id: editingOrder.supplier_tax_id,
                supplier: {
                  address_line1: editingOrder.supplier_address_line1,
                  address_line2: editingOrder.supplier_address_line2,
                  postal_code: editingOrder.supplier_postal_code,
                  city: editingOrder.supplier_city,
                  province: editingOrder.supplier_province,
                  email: editingOrder.supplier_email,
                  phone: editingOrder.supplier_phone,
                },
                status: "draft",
                order_date: editingOrder.order_date || new Date().toISOString().split("T")[0],
                expected_date: editingOrder.expected_date,
                subtotal: editingOrder.lines.reduce((sum, l) => sum + l.quantity_ordered * l.unit_price, 0),
                tax_amount: editingOrder.lines.reduce((sum, l) => sum + l.quantity_ordered * l.unit_price * (l.tax_rate / 100), 0),
                total_amount: calculateOrderTotal(),
                notes: editingOrder.notes,
                lines: editingOrder.lines.map((l, idx) => ({
                  id: l.id || String(idx),
                  order_id: editingId || "preview",
                  line_number: idx + 1,
                  code: l.code,
                  description: l.description,
                  unit: l.unit,
                  quantity_ordered: l.quantity_ordered,
                  quantity_received: 0,
                  unit_price: l.unit_price,
                  subtotal: l.quantity_ordered * l.unit_price,
                  tax_rate: l.tax_rate,
                  tax_amount: l.quantity_ordered * l.unit_price * (l.tax_rate / 100),
                  total: l.quantity_ordered * l.unit_price * (1 + l.tax_rate / 100),
                  is_fully_received: false,
                })),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              }}
              language="es"
            />
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <SupplierFormDialog
        open={newSupplierDialogOpen}
        onOpenChange={setNewSupplierDialogOpen}
        supplier={null}
        onSuccess={(newSupplier) => {
          setEditingOrder({
            ...editingOrder,
            supplier_id: newSupplier.id,
            supplier_name: newSupplier.legal_name,
            supplier_tax_id: newSupplier.tax_id || '',
          });
        }}
      />
    </DashboardLayout>
  );
}
